import { useTranslation } from "react-i18next";
import type { PointOfInterest, ThemeClasses } from "../types";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import { useStatusesStore } from "../../../../../../store/admin/statusesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../../../../../hooks/useIsReseller";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER, STATUS_CHIP } from "../../../../shared";

interface PointOfInterestRowCardProps {
  poi: PointOfInterest;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (poi: PointOfInterest) => void;
  onDetails: (poi: PointOfInterest) => void;
  onDelete?: (poi: PointOfInterest) => void;
}

const getCategoryIcon = (categoryId?: string): string => {
  switch (categoryId) {
    case "residence":
      return "ri-home-4-line";
    case "client":
      return "ri-user-line";
    case "work":
      return "ri-briefcase-line";
    case "warehouse":
      return "ri-store-2-line";
    case "office":
      return "ri-building-line";
    case "gas_station":
      return "ri-gas-station-line";
    default:
      return "ri-map-pin-line";
  }
};

const getCategoryLabel = (categoryId: string | undefined, t: (key: string) => string): string => {
  if (!categoryId) return t("pointsOfInterest.categories.other");
  return t(`pointsOfInterest.categories.${categoryId}`);
};

const getStatusColorByName = (statusName?: string): string => {
  const name = statusName?.toLowerCase() || "";
  if (name.includes("activ") && !name.includes("inactiv")) {
    return "text-primary-500 bg-primary-500/10";
  }
  if (name.includes("inactiv")) {
    return "text-red-500 bg-red-500/10";
  }
  return "text-gray-500 bg-gray-500/10";
};

export function PointOfInterestRowCard({
  poi,
  themeClasses,
  isDarkMode,
  onEdit,
  onDetails,
  onDelete,
}: PointOfInterestRowCardProps) {
  const { t } = useTranslation("adminSections");
  const statuses = useStatusesStore((state) => state.statuses);
  const disableCRUD = useDisableCRUD();

  // Obtener información del usuario para permisos
  const userId = localStorage.getItem("userId") || "";
  const userRoleId = localStorage.getItem("userRoleId") || "";
  const isReseller = userRoleId === ADMIN_RESELLER_ROLE_ID;

  // Verificar si puede editar: reseller puede todo, carrier solo sus creaciones
  const canEdit = isReseller || poi.createdBy === userId;

  // Obtener el status del POI desde el store
  const poiStatus = statuses.find((s) => s.id === poi.status_id);
  const statusLabel = poiStatus?.name
    ? getStatusHumanLabel(poiStatus.name, t)
    : t("pointsOfInterest.status.unknown");

  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
        {/* Left side - Icon and main info */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Category Icon */}
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? "bg-dark-surface" : "bg-gray-100"
            }`}
          >
            <i
              className={`${getCategoryIcon(poi.category_id)} text-base ${themeClasses.textSecondary}`}
            ></i>
          </div>

          {/* Name and Category */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={poi.name}>
                {poi.name}
              </h3>
              <span
                className={`${STATUS_CHIP} ${getStatusColorByName(poiStatus?.name)}`}
              >
                {statusLabel}
              </span>
            </div>
            <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
              {getCategoryLabel(poi.category_id, t)}
              {poi.address && ` • ${poi.address}`}
            </p>
          </div>
        </div>

        {/* Center - Coordinates and visibility */}
        <div className="hidden md:flex items-center gap-4 shrink-0">
          {/* Coordinates */}
          <div className="flex items-center gap-3">
            <div className="w-[4.5rem]">
              <p className={`text-xs ${themeClasses.textSecondary}`}>Lat</p>
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {poi.location?.latitude?.toFixed(4) || "-"}
              </p>
            </div>
            <div className="w-[4.5rem]">
              <p className={`text-xs ${themeClasses.textSecondary}`}>Lng</p>
              <p className={`text-sm font-medium ${themeClasses.text}`}>
                {poi.location?.longitude?.toFixed(4) || "-"}
              </p>
            </div>
          </div>

          {/* Visibility */}
          <div className="w-24">
            <span
              className={`${STATUS_CHIP} ${
                poi.shared
                  ? "text-blue-500 bg-blue-500/10"
                  : "text-amber-500 bg-amber-500/10"
              }`}
            >
              {poi.shared
                ? t("pointsOfInterest.visibility.shared")
                : t("pointsOfInterest.visibility.private")}
            </span>
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => onDetails(poi)}
            className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
            aria-label={t("pointsOfInterest.actions.details")}
            title={t("pointsOfInterest.actions.details")}
          >
            <i className="ri-eye-line"></i>
          </button>
          {canEdit && (
            <CRUDTooltip>
              <button
                onClick={() => onEdit(poi)}
                disabled={disableCRUD}
                className={`${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`}
                aria-label={t("pointsOfInterest.actions.edit")}
                title={t("pointsOfInterest.actions.edit")}
              >
                <i className="ri-edit-line"></i>
              </button>
            </CRUDTooltip>
          )}
          {canEdit && onDelete && (
            <button
              onClick={() => onDelete(poi)}
              className={ICON_BTN_DANGER}
              aria-label={t("pointsOfInterest.actions.delete", "Eliminar")}
              title={t("pointsOfInterest.actions.delete", "Eliminar")}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>
    </div>
  );
}
